import React, { Component } from 'react';
import axios from 'axios';
import moment from "moment";
import { APIURL } from '../../config';
import { Card, CardContent, TextField, InputAdornment, Box, CircularProgress, Typography, Chip, Button, Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import ProjectForm from '../../components/forms/ProjectForm/ProjectForm';

class HouseholdProjects extends Component {
    state = {
        searchTerm: '',
        projects: [],
        loading: true,
        householdMembers: [],
        selectedTab: 0,
        tabs: ['Ongoing', 'Completed'],
        onGoingProjs: [],
        completedProjs: [],
        showNewProject: false,
        employees: { list: [], empStatus: [], frequency: [], currency: [] },
        customers: { list: [] },
        locations: { list: [] },
        payers: { list: [] },
    }

    async componentDidMount() {
        await this.fetchData();
        await this.fetchDropdownData();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.household_id !== this.props.household_id) {
            await this.fetchData();
        }
    }

    async fetchDropdownData() {
        try {
            const [employeesRes, customersRes, locationsRes, payersRes, empStatusRes, frequencyRes] = await Promise.all([
                axios.get(`${APIURL}/dropdowns/dropdownType/employeeslist`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/dropdowns/dropdownType/customerslist`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/dropdowns/dropdownType/locationslist`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/dropdowns/dropdownType/payerslist`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/dropdowns/dropdownType/empStatus`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/config/defaults`, {
                    params: { feature_type: 'payrollFrequency' }
                })
            ]);

            this.setState({
                employees: {
                    list: employeesRes.data,
                    empStatus: empStatusRes.data,
                    frequency: frequencyRes.data,
                    currency: []
                },
                customers: { list: customersRes.data },
                locations: { list: locationsRes.data },
                payers: { list: payersRes.data }
            });
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }

    async fetchData() {
        if (!this.props.household_id) {
            this.setState({ loading: false, projects: [], householdMembers: [] });
            return;
        }

        this.setState({ loading: true });

        try {
            // First, fetch all household members (customers only)
            const membersResponse = await axios.get(`${APIURL}/households/members`, {
                params: { household_id: this.props.household_id }
            });
            const members = membersResponse.data || [];
            
            // Filter to only customers (not contacts)
            const customerMembers = members.filter(m => m.customer_id !== null);
            
            if (customerMembers.length === 0) {
                this.setState({ loading: false, projects: [], householdMembers: [] });
                return;
            }

            // Fetch projects for all customers in parallel
            const projectPromises = customerMembers.map(member =>
                axios.get(`${APIURL}/project`, { params: { cus_id: member.customer_id } })
                    .then(response => ({
                        customer_id: member.customer_id,
                        customer_name: member.cus_name,
                        projects: response.data || []
                    }))
                    .catch(error => {
                        console.error(`Error fetching projects for customer ${member.customer_id}:`, error);
                        return { customer_id: member.customer_id, customer_name: member.cus_name, projects: [] };
                    })
            );

            const projectsByCustomer = await Promise.all(projectPromises);
            
            // Flatten all projects and add customer info to each
            const allProjects = projectsByCustomer.flatMap(item =>
                item.projects.map(project => ({
                    ...project,
                    customer_id: item.customer_id,
                    customer_name: item.customer_name
                }))
            );

            // Fetch task data for each project
            const projectsWithTaskData = await Promise.all(
                allProjects.map(async (project) => {
                    try {
                        const completedTaskData = await axios.get(`${APIURL}/locate/TimeSpent`, {
                            params: { projId: project.pro_id, comp_id: this.props.user.comp_id }
                        });
                        return { ...project, taskData: completedTaskData.data };
                    } catch (error) {
                        console.error(`Error fetching task data for project ${project.pro_id}:`, error);
                        return { ...project, taskData: [] };
                    }
                })
            );

            this.setState({
                projects: projectsWithTaskData,
                householdMembers: customerMembers,
                loading: false
            }, () => {
                this.splitProjects();
            });
        } catch (error) {
            console.error('Error fetching household projects:', error);
            this.setState({ loading: false, projects: [], householdMembers: [] });
        }
    }

    handleSearchChange = (event) => {
        this.setState({ searchTerm: event.target.value });
    }

    selectTab = (tabIndex) => {
        this.setState({ selectedTab: tabIndex });
    }

    splitProjects = () => {
        const { projects } = this.state;
        
        let onGoing = projects.filter((project) => {
            const endDate = moment(project.pro_ends_on);
            const startDate = moment(project.pro_starts_on);
            const today = moment();
            
            // A project is ongoing if:
            // 1. End date is today or in the future, OR
            // 2. Project has no valid dates (null/undefined start or end dates), OR
            // 3. Start date is invalid (before 1900)
            const hasValidEndDate = project.pro_ends_on && endDate.isValid();
            const hasValidStartDate = project.pro_starts_on && startDate.isValid();
            const endDateIsAfterToday = hasValidEndDate && endDate.isSameOrAfter(today, 'day');
            const hasInvalidDates = !hasValidStartDate || !hasValidEndDate || startDate.isSameOrBefore("1900-01-01");
            
            return endDateIsAfterToday || hasInvalidDates;
        });
        
        let completed = projects.filter((project) => {
            const endDate = moment(project.pro_ends_on);
            const startDate = moment(project.pro_starts_on);
            const today = moment();
            
            // A project is completed if:
            // 1. Has valid start and end dates, AND
            // 2. Start date is after 1900, AND
            // 3. End date is before today
            const hasValidEndDate = project.pro_ends_on && endDate.isValid();
            const hasValidStartDate = project.pro_starts_on && startDate.isValid();
            const endDateIsBeforeToday = hasValidEndDate && endDate.isBefore(today, 'day');
            const startDateIsAfter1900 = hasValidStartDate && startDate.isAfter("1900-01-01");
            
            return hasValidStartDate && hasValidEndDate && startDateIsAfter1900 && endDateIsBeforeToday;
        });

        this.setState({ onGoingProjs: onGoing, completedProjs: completed });
    }

    getFilteredProjects = () => {
        const { selectedTab, onGoingProjs, completedProjs, searchTerm } = this.state;
        const projectsToFilter = selectedTab === 0 ? onGoingProjs : completedProjs;
        
        if (!searchTerm.trim()) return projectsToFilter;
        
        const lowerSearch = searchTerm.toLowerCase();
        return projectsToFilter.filter(project =>
            project.pro_title?.toLowerCase().includes(lowerSearch) ||
            project.pro_description?.toLowerCase().includes(lowerSearch) ||
            project.customer_name?.toLowerCase().includes(lowerSearch)
        );
    }

    render() {
        const { loading, searchTerm, selectedTab, tabs, onGoingProjs, completedProjs } = this.state;
        const filteredProjects = this.getFilteredProjects();

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search projects..."
                        value={searchTerm}
                        onChange={this.handleSearchChange}
                        style={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => this.setState({ showNewProject: true })}
                    >
                        New Project
                    </Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <div 
                        style={{
                            border: '1px solid #99BDF3',
                            borderRadius: '15px',
                            padding: '8px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: tabs[selectedTab] === 'Ongoing' ? '#DBE9FF' : 'white',
                            color: tabs[selectedTab] === 'Ongoing' ? '#000000' : '#737373',
                            fontSize: '15px',
                            transition: 'all 0.2s'
                        }}
                        onClick={() => this.selectTab(0)}
                    >
                        <div>Ongoing</div>
                        {tabs[selectedTab] === 'Ongoing' && (
                            <div style={{
                                width: '20px',
                                height: '20px',
                                background: '#9FC4FE',
                                fontSize: '12px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '16px'
                            }}>
                                {onGoingProjs.length}
                            </div>
                        )}
                    </div>
                    <div 
                        style={{
                            border: '1px solid #99BDF3',
                            borderRadius: '15px',
                            padding: '8px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: tabs[selectedTab] === 'Completed' ? '#DBE9FF' : 'white',
                            color: tabs[selectedTab] === 'Completed' ? '#000000' : '#737373',
                            fontSize: '15px',
                            transition: 'all 0.2s'
                        }}
                        onClick={() => this.selectTab(1)}
                    >
                        <div>Completed</div>
                        {tabs[selectedTab] === 'Completed' && (
                            <div style={{
                                width: '20px',
                                height: '20px',
                                background: '#9FC4FE',
                                fontSize: '12px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '16px'
                            }}>
                                {completedProjs.length}
                            </div>
                        )}
                    </div>
                </div>

                {filteredProjects.length === 0 ? (
                    <Box sx={{ textAlign: 'center', mt: 5 }}>
                        <Typography variant="h6" color="textSecondary">
                            No projects found for this household
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {this.state.householdMembers.length === 0
                                ? 'Add customers to this household to see their projects.'
                                : 'The customers in this household do not have any projects yet.'}
                        </Typography>
                    </Box>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                        {filteredProjects.map(project => (
                            <Card
                                key={project.pro_id}
                                onClick={() => this.props.history.push({
                                    pathname: '/projects',
                                    projectsProps: { pro_id: project.pro_id, cus_id: project.customer_id }
                                })}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        this.props.history.push({
                                            pathname: '/projects',
                                            projectsProps: { pro_id: project.pro_id, cus_id: project.customer_id }
                                        });
                                    }
                                }}
                                role="link"
                                tabIndex={0}
                                style={{
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    height: '100%',
                                    minHeight: '220px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: '#d4f1f9',
                                    cursor: 'pointer'
                                }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                    }}
                                >
                                    <CardContent style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <Typography variant="h6" component="div" style={{ fontWeight: 600, color: '#257bcd' }}>
                                                {project.pro_title}
                                            </Typography>
                                            <Chip
                                                label={project.pro_is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={project.pro_is_active ? 'primary' : 'default'}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                                            <PersonIcon fontSize="small" style={{ color: '#666' }} />
                                            <Typography variant="body2" color="textSecondary">
                                                {project.customer_name}
                                            </Typography>
                                        </div>

                                        {project.pro_description && (
                                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                                                {project.pro_description.length > 100
                                                    ? `${project.pro_description.substring(0, 100)}...`
                                                    : project.pro_description}
                                            </Typography>
                                        )}

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '12px' }}>
                                            <CalendarTodayIcon fontSize="small" style={{ color: '#666' }} />
                                            <Typography variant="caption" color="textSecondary">
                                                {project.pro_starts_on
                                                    ? moment(project.pro_starts_on).format('MMM DD, YYYY')
                                                    : 'No start date'}
                                                {' → '}
                                                {project.pro_ends_on
                                                    ? moment(project.pro_ends_on).format('MMM DD, YYYY')
                                                    : 'Ongoing'}
                                            </Typography>
                                        </div>

                                        {project.taskData && project.taskData.length > 0 && (
                                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {project.taskData.length} task{project.taskData.length !== 1 ? 's' : ''}
                                                </Typography>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                        ))}
                    </div>
                )}

                <Dialog 
                    fullWidth={true} 
                    maxWidth={'sm'} 
                    open={this.state.showNewProject} 
                    onClose={() => this.setState({ showNewProject: false })}
                >
                    <DialogActions>
                        <h4 style={{ marginLeft: '20px', marginTop: '10px' }}>New Project</h4>
                        <hr style={{ marginBottom: '10px', marginTop: '10px', backgroundColor: '#e2ecfa' }}></hr>
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            sx={{ p: 0.25 }}
                            onClick={() => this.setState({ showNewProject: false })}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogActions>
                    <DialogContent sx={{ paddingTop: '0px' }}>
                        <ProjectForm
                            {...this.props}
                            customerList={this.state.customers.list}
                            empList={this.state.employees.list}
                            payerList={this.state.payers.list}
                            locList={this.state.locations.list}
                            empStatus={this.state.employees.empStatus}
                            currency={this.state.employees.currency}
                            frequency={this.state.employees.frequency}
                            comp_id={this.props.user?.comp_id}
                            customerPage={false}
                            customer_id={null}
                            household_id={this.props.household_id}
                            onSubmit={() => {
                                this.setState({ showNewProject: false });
                                this.fetchData();
                            }}
                        />
                    </DialogContent>
                </Dialog>
            </div>
        );
    }
}

export default HouseholdProjects;
